import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useFamilyStore } from '../stores/familyStore';
import {
  getUserRoleFromMembers,
  roleHasPermission,
  roleHasMinimumLevel,
} from '../utils/accessControl';
import type { Permission, FamilyRole } from '../utils/accessControl';
import { useEntitlements } from './useEntitlements';

/**
 * Hook for checking user permissions in family context.
 * With Convex, members data is always fresh via FamilyDataSync reactive queries.
 * No async fetching needed — role is derived synchronously from store.
 */
export const usePermissions = () => {
  const { user } = useAuthStore();
  const { family, members } = useFamilyStore();

  // Derive role synchronously from members in store
  const userRole = useMemo<FamilyRole | null>(() => {
    if (!user || !family) return null;
    return getUserRoleFromMembers(user.id, members);
  }, [user, family, members]);

  const entitlements = useEntitlements(family?.id);

  // Downgrade Parent B to Viewer if plan is Basic (Solo)
  const effectiveRole = (family && entitlements.planId === 'basic' && userRole === 'parent_b')
    ? 'child_viewer'
    : userRole;

  const checkPermission = async (permission: Permission): Promise<boolean> => {
    if (!effectiveRole) return false;
    return roleHasPermission(effectiveRole, permission);
  };

  const checkMinimumRole = async (minimumRole: FamilyRole): Promise<boolean> => {
    if (!effectiveRole) return false;
    return roleHasMinimumLevel(effectiveRole, minimumRole);
  };

  return {
    userRole: effectiveRole,
    loading: false, // No async loading needed with Convex
    error: null,
    checkPermission,
    checkMinimumRole,
    isParentA: effectiveRole === 'parent_a',
    isParentB: effectiveRole === 'parent_b',
    isParent: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    isObserver: effectiveRole === 'child_viewer',

    canEditCalendar: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canViewCalendar: true,
    canEditFamilySettings: effectiveRole === 'parent_a',
    canViewFamilySettings: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canManageChildren: effectiveRole === 'parent_a',
    canViewChildren: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canManageAccess: effectiveRole === 'parent_a',
    canChangePassword: true,
    canChangeAppearance: true,
    canViewNotifications: true,
    canEditNotifications: false,

    canAccessGeneralTab: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canAccessChildrenTab: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canAccessAccessTab: effectiveRole === 'parent_a' || effectiveRole === 'parent_b',
    canAccessPasswordTab: true,
    canAccessAppearanceTab: true,
    canAccessNotificationsTab: true,
  };
};
